import { useEffect, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { Appearance } from '@stripe/stripe-js'
import { getStripe } from '../../lib/stripe'
import { Button, Spinner } from '../ui'

/**
 * The ONLY file that imports @stripe/react-stripe-js. Wraps the Payment
 * Element in an Elements provider fed by the memoized lib/stripe loader, and
 * confirms in-place (`redirect: 'if_required'`) so only 3DS-style methods
 * bounce through the /wallet return URL.
 */

// Hex approximations of the OKLCH ember tokens (entrypoints/fireside.css) —
// Stripe's appearance API wants plain CSS colors and paints inside its own
// iframe, out of reach of our stylesheet. Deliberately minimal (theme night).
const appearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#f59e0b', // ~ember-500
    colorBackground: '#191411', // ~surface-sunken
    colorText: '#eee6d8', // ~text
    colorTextSecondary: '#b6ad9e', // ~text-muted
    colorDanger: '#ee6352', // ~danger-fg
    borderRadius: '10px', // ~radius-control
    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
  },
}

interface TopUpPaymentFormProps {
  clientSecret: string
  /** Absolute walletReturnUrl(topUpId) — the /wallet 3DS landing page. */
  returnUrl: string
  /**
   * Fired on IN-PLACE confirmation success (no redirect). This only advances
   * the UI — the wallet credits ONLY when the ledger does (webhook/poll), so
   * the parent keeps polling GET /wallet/top_ups/:id for `credited`.
   */
  onConfirmed: () => void
  onCancel: () => void
}

export function TopUpPaymentForm({ clientSecret, returnUrl, onConfirmed, onCancel }: TopUpPaymentFormProps) {
  // getStripe() resolves null when no publishable key is configured; render
  // the honest degraded state rather than an Elements form that never loads.
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let active = true
    getStripe().then(
      (stripe) => {
        if (active && !stripe) setUnavailable(true)
      },
      () => {
        if (active) setUnavailable(true)
      },
    )
    return () => {
      active = false
    }
  }, [])

  if (unavailable) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-warning-fg">
          Payments aren&rsquo;t configured in this environment.
        </p>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onCancel}>
            Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Elements stripe={getStripe()} options={{ clientSecret, appearance }}>
      <PaymentFormInner returnUrl={returnUrl} onConfirmed={onConfirmed} onCancel={onCancel} />
    </Elements>
  )
}

function PaymentFormInner({ returnUrl, onConfirmed, onCancel }: Omit<TopUpPaymentFormProps, 'clientSecret'>) {
  const stripe = useStripe()
  const elements = useElements()
  const [ready, setReady] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements || submitting) return
    setSubmitting(true)
    setError('')
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      // Stay in-page for plain card payments; only redirect-based methods
      // (3DS challenges) leave for returnUrl.
      redirect: 'if_required',
    })
    if (result.error) {
      const { type, message } = result.error
      setError(
        (type === 'card_error' || type === 'validation_error') && message
          ? message
          : 'Something went wrong confirming the payment — try again.',
      )
      setSubmitting(false)
      return
    }
    // In-place success. Money truth still arrives via the ledger.
    onConfirmed()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-label="Card details">
      {!ready && (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      )}
      <PaymentElement onReady={() => setReady(true)} />
      {error && (
        <p className="text-sm text-danger-fg" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
          Back
        </Button>
        <Button type="submit" loading={submitting} disabled={!stripe || !elements || !ready || submitting}>
          Pay now
        </Button>
      </div>
    </form>
  )
}
